import * as React from 'react';
import {
  ToastBar,
  resolveValue,
  useToaster,
  type ToasterProps,
  type ToastPosition,
} from 'react-hot-toast';

// ثوابت مشتركة لاستخدامها في كل أجزاء التطبيق
export const TOAST_STYLE = {
  fontFamily: 'inherit',
  borderRadius: '10px',
  background: 'oklch(0.3211 0 0)',
  color: 'oklch(1 0 0)',
  fontSize: '13px',
} as const;

export const TOAST_SUCCESS_ICON = {
  primary: 'oklch(73.31% 0.198 142.15)',
  secondary: 'oklch(1 0 0)',
} as const;

export const TOAST_ERROR_ICON = {
  primary: 'oklch(0.61 0.25 28.5)',
  secondary: 'oklch(1 0 0)',
} as const;

// safe area للـ iOS notch وDynamic Island — يُرجع CSS value صحيح
// viewport-fit=cover يجب أن يكون موجوداً في index.html
const SAFE_TOP = 'max(16px, env(safe-area-inset-top, 16px))';
const SIDE_OFFSET = 16;

/** أعلى حاوية الـ toast: تحت شريط أيقونة السلة في الهيدر (٢٠+٥٠ بكسل + هامش) */
export const TOASTER_TOP_BELOW_STORE_HEADER = `calc(${SAFE_TOP} + 52px)`;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const getPositionStyle = (
  position: ToastPosition,
  offset: number,
): React.CSSProperties => {
  const top = position.includes('top');
  const verticalStyle: React.CSSProperties = top ? { top: 0 } : { bottom: 0 };
  const horizontalStyle: React.CSSProperties = position.includes('center')
    ? { justifyContent: 'center' }
    : position.includes('right')
      ? { justifyContent: 'flex-end' }
      : {};
  return {
    left: 0,
    right: 0,
    display: 'flex',
    position: 'absolute',
    transition: prefersReducedMotion()
      ? undefined
      : `all 230ms cubic-bezier(.21,1.02,.73,1)`,
    transform: `translateY(${offset * (top ? 1 : -1)}px)`,
    ...verticalStyle,
    ...horizontalStyle,
  };
};

type ToastWrapperProps = {
  id: string;
  className?: string;
  style?: React.CSSProperties;
  onHeightUpdate: (id: string, height: number) => void;
  children: React.ReactNode;
};

const ToastWrapper = ({
  id,
  className,
  style,
  onHeightUpdate,
  children,
}: ToastWrapperProps) => {
  // حفظ observer reference لإيقافه عند unmount (منع memory leak)
  const observerRef = React.useRef<MutationObserver | null>(null);

  const ref = React.useCallback(
    (el: HTMLElement | null) => {
      // تنظيف الـ observer القديم عند تغيّر الـ element
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (el) {
        const updateHeight = () => {
          const height = el.getBoundingClientRect().height;
          onHeightUpdate(id, height);
        };
        updateHeight();
        const observer = new MutationObserver(updateHeight);
        observer.observe(el, { subtree: true, childList: true, characterData: true });
        observerRef.current = observer;
      }
    },
    [id, onHeightUpdate],
  );

  // تنظيف عند unmount
  React.useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

function useFinePointerHover(): boolean {
  const [fine, setFine] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  });

  React.useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const fn = () => setFine(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  return fine;
}

/**
 * Toaster يتوافق مع Safari/iOS: react-hot-toast يوقف المؤقت عند mouseenter ويستأنفه عند mouseleave.
 * على الشاشات اللمسية يُستدعى أحياناً enter دون leave فيبقى pausedAt نشطاً والإشعار لا يُغلق.
 * نربط الإيقاف المؤقت فقط عند (hover: hover) و(pointer: fine)، ونُلغي أي إيقاف عند عودة التبويب.
 */
export function AppToaster({
  reverseOrder,
  position = 'top-center',
  toastOptions,
  gutter,
  children,
  toasterId,
  containerStyle,
  containerClassName,
}: ToasterProps) {
  const { toasts, handlers } = useToaster(toastOptions, toasterId);
  const finePointerHover = useFinePointerHover();

  // استخدام ref لـ handlers.endPause لتجنب إعادة تسجيل listener عند كل render
  const endPauseRef = React.useRef(handlers.endPause);
  React.useEffect(() => {
    endPauseRef.current = handlers.endPause;
  });

  React.useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        endPauseRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []); // dependency array فارغ — يُسجَّل مرة واحدة فقط

  return (
    // role="region" + aria-live يجعل قارئات الشاشة (VoiceOver/TalkBack) تُعلن عن الإشعارات
    <div
      role="region"
      aria-live="polite"
      aria-label="الإشعارات"
      data-rht-toaster={toasterId || ''}
      style={{
        position: 'fixed',
        zIndex: 9999,
        top: SAFE_TOP,
        left: SIDE_OFFSET,
        right: SIDE_OFFSET,
        bottom: SIDE_OFFSET,
        pointerEvents: 'none',
        ...containerStyle,
      }}
      className={containerClassName}
      onMouseEnter={finePointerHover ? handlers.startPause : undefined}
      onMouseLeave={finePointerHover ? handlers.endPause : undefined}
    >
      {toasts.map((t) => {
        const toastPosition = t.position || position;
        const offset = handlers.calculateOffset(t, {
          reverseOrder,
          gutter,
          defaultPosition: position,
        });
        const positionStyle = getPositionStyle(toastPosition, offset);

        return (
          <ToastWrapper
            id={t.id}
            key={t.id}
            onHeightUpdate={handlers.updateHeight}
            className={t.visible ? 'rht-toast-row-visible' : undefined}
            style={{ ...positionStyle, pointerEvents: 'auto' }}
          >
            {t.type === 'custom' ? (
              resolveValue(t.message, t)
            ) : children ? (
              children(t)
            ) : (
              <ToastBar toast={t} position={toastPosition} />
            )}
          </ToastWrapper>
        );
      })}
    </div>
  );
}
